import { useEffect, useRef, useState, useCallback } from "react";
import gsap from "gsap";
import "./CertificationOptimized.css";

const CERTIFICATES = [
  {
    id: 1,
    image: "https://firebasestorage.googleapis.com/v0/b/aviatricks-4ffdf.firebasestorage.app/o/FLYTUTOR%2Fcertifications%2Fcertificate1.jpeg?alt=media&token=1e39e49a-39ca-4365-b6cc-88808329b0e8",
    alt: "DGCA Certificate 1",
    title: "DGCA Authorization Certificate",
    description: "Official DGCA approval for drone training operations"
  },
  {
    id: 2,
    image: "https://firebasestorage.googleapis.com/v0/b/aviatricks-4ffdf.firebasestorage.app/o/FLYTUTOR%2Fcertifications%2Fcertificate2.jpeg?alt=media&token=645419be-2433-459b-832f-02297c2a455b",
    alt: "DGCA Certificate 2",
    title: "RPTO Certification",
    description: "Recognized Remote Pilot Training Organization"
  }
];

const CERTIFICATION_POINTS = [
  { text: "DGCA Approved Drone Training Institute", icon: "🏛️" },
  { text: "RPTO Authorization No: RA11240000026", icon: "📜" },
  { text: "DGCA Authorisation No: 61/2024", icon: "✅" },
  { text: "Conditions: VLOS (Rotorcraft)", icon: "🚁" },
  { text: "Class: Small & Medium", icon: "📊" }
];

export default function CertificationOptimized() {
  const [selectedCert, setSelectedCert] = useState(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  const openModal = useCallback((cert) => {
    setSelectedCert(cert);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeModal = useCallback(() => {
    setSelectedCert(null);
    document.body.style.overflow = '';
  }, []);

  useEffect(() => {
    const handleEsc = (e) => e.key === 'Escape' && closeModal();
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [closeModal]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          gsap.fromTo(
            ".cert-animate",
            { opacity: 0, y: 30 },
            {
              opacity: 1,
              y: 0,
              duration: 0.6,
              stagger: 0.1,
              ease: "power2.out"
            }
          );
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  return (
    <>
      <section ref={sectionRef} className="cert-section">
        <div className="cert-bg">
          <div className="cert-orb cert-orb-1" />
          <div className="cert-orb cert-orb-2" />
          <div className="cert-orb cert-orb-3" />
        </div>

        <div className="cert-container">
          <div className="cert-header">
            <span className="cert-subtitle cert-animate">OFFICIAL RECOGNITION</span>
            <h2 className="cert-heading cert-animate">
              Our <span className="cert-highlight">Certification</span>
            </h2>
            <p className="cert-subtitle-text cert-animate">
              Fully compliant with DGCA regulations, our training programs are conducted 
              by Government-certified instructors at an authorized Remote Pilot Training Organization.
            </p>
            <div className="cert-badge cert-animate">
              <span className="cert-badge-icon">🏆</span>
              <span className="cert-badge-text">Officially Recognized</span>
            </div>
          </div>

          {/* Grid */}
          <div className="cert-grid">
            {/* Features */}
            <div className="cert-features">
              {CERTIFICATION_POINTS.map((point, idx) => (
                <div key={idx} className="cert-feature cert-animate">
                  <span className="cert-icon">{point.icon}</span>
                  <span className="cert-feature-text">{point.text}</span>
                </div>
              ))}
              <div className="cert-info cert-animate">
                <div className="cert-info-item">
                  <span className="cert-info-label">VALIDITY</span>
                  <span className="cert-info-value">2024 - 2034</span>
                </div>
                <div className="cert-info-item">
                  <span className="cert-info-label">CATEGORY</span>
                  <span className="cert-info-value">RPTO CLASS 2</span>
                </div>
              </div>
            </div>

            {/* Certificates */}
            <div className="cert-gallery">
              {CERTIFICATES.map((cert) => (
                <div key={cert.id} className="cert-card cert-animate" onClick={() => openModal(cert)}>
                  <div className="cert-card-image">
                    <img src={cert.image} alt={cert.alt} loading="lazy" />
                    <div className="cert-card-overlay">
                      <h3>{cert.title}</h3>
                      <p>{cert.description}</p>
                      <button className="cert-view-btn">
                        <span>🔍</span> View Certificate
                      </button>
                    </div>
                  </div>
                  <div className="cert-card-footer">
                    <span className="cert-card-badge">OFFICIAL</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Modernized Certification Modal */}
      {selectedCert && (
        <div className="cm-modal-overlay" onClick={closeModal}>
          <div className="cm-modal-container" onClick={(e) => e.stopPropagation()}>
            <button className="cm-close-btn" onClick={closeModal}>&times;</button>
            <div className="cm-layout">
              {/* IMAGE SIDE */}
              <div className="cm-visual">
                <img src={selectedCert.image} alt={selectedCert.alt} />
              </div>
              
              {/* DETAIL SIDE */}
              <div className="cm-details">
                <div className="cm-header">
                  <span className="cm-badge">AUTHENTICATED DOCUMENT</span>
                  <h3>{selectedCert.title}</h3>
                  <p>{selectedCert.description}</p>
                </div>

                <div className="cm-entry-grid">
                  <div className="cm-entry">
                    <span>Issuing Authority</span>
                    <strong>DGCA India (Govt. of India)</strong>
                  </div>
                  <div className="cm-entry">
                    <span>Validation No.</span>
                    <strong>61/2024 - RPTO License</strong>
                  </div>
                  <div className="cm-entry">
                    <span>Tenure</span>
                    <strong>2024 - 2034 (Renewable)</strong>
                  </div>
                  <div className="cm-entry">
                    <span>Training Class</span>
                    <strong>Small & Medium Category</strong>
                  </div>
                </div>

                <div className="cm-footer">
                  <button className="cm-btn-primary" onClick={() => window.open(selectedCert.image, '_blank')}>
                    Download Full Resolution Document
                  </button>
                  <div className="cm-trust-footer">
                    <span>✓ Encrypted Storage</span>
                    <span>✓ Instant Verification</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}